import { Link, useNavigate } from "react-router-dom";
import { LogOut, ChevronRight, KeyRound } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/i18n/I18nContext";
import { LangToggle } from "@/components/LangToggle";
import { StatsGrid } from "@/pages/HistoryPage";
import { ContactWhatsApp } from "@/components/ContactWhatsApp";
import { ReferralCard } from "@/components/ReferralCard";

const Row = ({ label, value, testId }) => (
  <div className="flex items-center justify-between gap-4 border-b border-white/5 py-4 last:border-0">
    <span className="text-sm text-slate-400">{label}</span>
    <span className="text-right text-sm font-semibold" data-testid={testId}>{value}</span>
  </div>
);

export default function ProfilePage() {
  const { user, logout } = useAuth();
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const fmt = (d) => (d ? new Date(d).toLocaleDateString(lang === "en" ? "en-GB" : "fr-FR") : "—");
  return (
    <div className="mx-auto max-w-2xl space-y-5 rise" data-testid="profile-page">
      <div className="flex items-center gap-4">
        <div className="grid h-16 w-16 place-items-center rounded-2xl bg-amber-500 font-display text-2xl font-extrabold text-[#0B0D12]">
          {user.pseudo.slice(0, 2).toUpperCase()}
        </div>
        <div>
          <h1 className="font-display text-2xl font-extrabold" data-testid="profile-pseudo">{user.pseudo}</h1>
          <p className="text-sm text-slate-500">{t("profile.memberSince")} {fmt(user.created_at)}</p>
        </div>
      </div>
      <StatsGrid />
      <ReferralCard />
      <Link to="/history" className="surface flex min-h-[56px] items-center justify-between px-5 text-sm font-semibold" data-testid="profile-history-link">
        {t("history.title")} <ChevronRight className="h-4 w-4 text-slate-500" />
      </Link>
      <Link to="/account" className="surface flex min-h-[56px] items-center justify-between px-5 text-sm font-semibold" data-testid="profile-account-link">
        {t("resp.title")} <ChevronRight className="h-4 w-4 text-slate-500" />
      </Link>
      <section className="surface px-5">
        {user.email && <Row label={t("auth.email")} value={user.email} testId="profile-email" />}
        {user.phone && <Row label={t("auth.phone")} value={<span className="font-mono">{user.phone}</span>} testId="profile-phone" />}
        <Row label={t("profile.birth")} value={fmt(user.birth_date)} testId="profile-birthdate" />
        <Row label={t("profile.kyc")} value={<span className="text-amber-300">{t(`resp.kyc_${user.kyc_status || "none"}`)}</span>} testId="profile-kyc" />
        <div className="flex items-center justify-between py-4">
          <span className="text-sm text-slate-400">{t("profile.language")}</span>
          <LangToggle />
        </div>
      </section>
      <Link to="/change-password" className="surface flex items-center gap-3 p-5 transition-colors hover:bg-white/[0.06]" data-testid="profile-change-password-link">
        <KeyRound className="h-5 w-5 text-amber-400" />
        <span className="flex-1 font-semibold">{t("pwd.title")}</span>
        <ChevronRight className="h-4 w-4 text-slate-500" />
      </Link>
      <ContactWhatsApp testid="profile-contact" />
      <section className="surface px-5">
        <p className="pt-4 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">{t("profile.legal")}</p>
        {["terms", "rules", "privacy", "responsible"].map((l) => (
          <Link key={l} to={`/legal/${l}`} className="flex min-h-[52px] items-center justify-between border-b border-white/5 text-sm last:border-0" data-testid={`profile-legal-${l}`}>
            {t(`legal.${l}`)} <ChevronRight className="h-4 w-4 text-slate-500" />
          </Link>
        ))}
      </section>
      <button
        onClick={async () => {
          await logout();
          navigate("/");
        }}
        className="flex h-14 w-full items-center justify-center gap-2 rounded-full border border-red-500/30 font-semibold text-red-300 transition-colors hover:bg-red-500/10"
        data-testid="logout-btn"
      >
        <LogOut className="h-5 w-5" /> {t("auth.logout")}
      </button>
    </div>
  );
}
